/**
 * @since 2025-02-19
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { useNotification } from "components/Shared/Notifications/Notifications";
import { useEffect, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { IoTerminalOutline } from "react-icons/io5";
import { getLogList, AuthenticationError } from "../../../dataHandling";
import CellDetails from "./CellDetails";
import "./CellJob.scss";
import JobActions from "./JobActions";
import LogViewer from "./LogViewer";

const CellJob = ({ job, dictJobs, onStatusChangeRequest, onAuthError, idToken, getToken, colSpan = 8 }) => {
  const [selectedPanel, setSelectedPanel] = useState("log");
  const [isExpanded, setIsExpanded] = useState(false);
  const [listLogFiles, setListLogFiles] = useState([]);
  const { notifyError } = useNotification();

  useEffect(() => {
    // Load
    const loadListLogFiles = async () => {
      try {
        const response = await getLogList(job.SchedulerName, job.Name, idToken, getToken);
        setListLogFiles(JSON.parse(response));
      } catch (error) {
        if (error instanceof AuthenticationError) {
          if (onAuthError) {
            onAuthError(error.message);
          }
        } else {
          console.error("Error loading log files:", error);
          notifyError(`Error loading log files: ${error}`);
        }
        setListLogFiles([]);
      }
    };
    loadListLogFiles();
    // job.ApplicationId changes on every new run, so a re-run refreshes the log
    // list (and therefore the file selector) without collapsing the cell.
  }, [job.SchedulerName, job.Name, job.ApplicationId, onAuthError, idToken, getToken, notifyError]);

  useEffect(() => {
    if (isExpanded) {
      // Disable scrolling on body when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isExpanded]);

  const handleExpand = () => {
    setIsExpanded(true);
  };
  
  const closeExpand = () => {
    setIsExpanded(false);
  };

  // Create a single shared content renderer function
  const renderContent = () => {
    return (
      <>
        {selectedPanel === "log" && (
          <div className="cell-job__content__log-pane">
            <LogViewer
              job={job}
              idToken={idToken}
              onExpandClick={isExpanded ? closeExpand : handleExpand}
              onAuthError={onAuthError}
              listLogFiles={listLogFiles}
            />
          </div>
        )}
        {selectedPanel === "info" && 
          <CellDetails 
            job={job}
            dictJobs={dictJobs}
            onExpandClick={isExpanded ? closeExpand : handleExpand}
          />
        }
      </>
    );
  };

  // Create a single shared sidebar renderer function
  const renderSidebar = () => {
    return (
      <>
        <JobActions job={job} onStatusChangeRequest={onStatusChangeRequest} />

        <button
          className={`cell-job__tab-btn ${
            selectedPanel === "log" ? "active" : ""
          }`}
          onClick={() => setSelectedPanel("log")}
          aria-label="Log"
        >
          <IoTerminalOutline size={22} />
        </button>
        <button
          className={`cell-job__tab-btn ${
            selectedPanel === "info" ? "active" : ""
          }`}
          onClick={() => setSelectedPanel("info")}
          aria-label="Info"
        >
          <FaInfoCircle size={22} />
        </button>
      </>
    );
  };

  // Render the cell job component regardless of expanded state
  const renderCellJob = (expanded = false) => (
    <div className={`cell-job ${expanded ? "cell-job--expanded" : ""}`}>
      <div className="cell-job__sidebar">{renderSidebar()}</div>
      <div className="cell-job__content">{renderContent()}</div>
    </div>
  );

  return (
    <>
      {/* The row containing CellJob - hide when expanded */}
      {!isExpanded && (
        <tr>
          <td colSpan={colSpan} style={{ padding: 0 }}>
            {renderCellJob(false)}
          </td>
        </tr>
      )}

      {/* Expanded (full-screen) CellJob */}
      {isExpanded && (
        <div className="modal-maximized-celljob-overlay" onClick={closeExpand}>
          <div
            className="modal-maximized-celljob"
            onClick={(e) => e.stopPropagation()}
          >
            {renderCellJob(true)}
          </div>
        </div>
      )}
    </>
  );
};

export default CellJob;
